import { useState, useEffect } from 'react';
import { Bot, Brain, FileText, Mail, BarChart3, Lightbulb, Loader2, CheckCircle2, Sparkles, CreditCard } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { KiaanLockedCard } from '@/components/shared/KiaanLockedCard';

interface CreditBalance {
  balance: number;
  totalAllocated: number;
}

const FEATURE_PRICING: Record<string, number> = {
  lead_score: 2,
  proposal_draft: 5,
  email_draft: 3,
  weekly_summary: 5,
  suggestions: 1,
};

export function AIAssistantPage() {
  const { aiAssistantName } = useTenantConfig();
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [kiaanLocked, setKiaanLocked] = useState<{
    message: string; ctaUrl: string; ctaLabel: string;
  } | null>(null);

  // Weekly Summary state
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState<any>(null);

  // Lead Scoring state
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreResult, setScoreResult] = useState<any>(null);

  // Email Draft state
  const [emailContact, setEmailContact] = useState('');
  const [emailPurpose, setEmailPurpose] = useState('follow_up');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState<any>(null);

  // Listen for 402 kiaan_subscription_required (dispatched by api.ts interceptor)
  useEffect(() => {
    const handler = (e: CustomEvent<{ message: string; ctaUrl: string; ctaLabel: string }>) => {
      setKiaanLocked(e.detail);
    };
    window.addEventListener('aura:kiaan:locked', handler as EventListener);
    return () => window.removeEventListener('aura:kiaan:locked', handler as EventListener);
  }, []);

  // Fetch credits and contacts
  useEffect(() => {
    api.get('/credits/balance').then(r => {
      setCredits(r.data.data);
    }).catch(() => {
      setCredits({ balance: 0, totalAllocated: 0 });
    }).finally(() => setLoadingCredits(false));

    api.get('/contacts?limit=100').then(r => {
      setContacts(r.data.data?.contacts ?? r.data.data ?? []);
    }).catch(() => {});
  }, []);

  const refreshCredits = () => {
    api.get('/credits/balance').then(r => setCredits(r.data.data)).catch(() => {});
  };

  // Weekly Summary
  const handleWeeklySummary = async () => {
    setSummaryLoading(true);
    setSummary(null);
    try {
      const res = await api.post('/ai/weekly-summary');
      setSummary(res.data.data);
      refreshCredits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Lead Score
  const handleLeadScore = async () => {
    if (!selectedContact) return;
    setScoreLoading(true);
    setScoreResult(null);
    try {
      const res = await api.post('/ai/lead-score', { contactId: selectedContact });
      setScoreResult(res.data.data);
      refreshCredits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to score lead');
    } finally {
      setScoreLoading(false);
    }
  };

  // Email Draft
  const handleEmailDraft = async () => {
    if (!emailContact) return;
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const res = await api.post('/ai/email-draft', { contactId: emailContact, purpose: emailPurpose });
      setEmailResult(res.data.data);
      refreshCredits();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to draft email');
    } finally {
      setEmailLoading(false);
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="mx-auto max-w-5xl">
      {/* 402 Kiaan locked banner */}
      {kiaanLocked && (
        <div className="mb-4">
          <KiaanLockedCard
            message={kiaanLocked.message}
            ctaUrl={kiaanLocked.ctaUrl}
            ctaLabel={kiaanLocked.ctaLabel}
            onDismiss={() => setKiaanLocked(null)}
          />
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={24} className="text-[var(--saffron-600)]" />
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{aiAssistantName}, your AI assistant</h1>
          </div>
          <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
            Intelligent automation and insights powered by AI
          </p>
        </div>
        {/* Credit Balance */}
        <div className="rounded-xl border p-4 text-center" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>AI Credits</span>
          </div>
          {loadingCredits ? (
            <Loader2 size={16} className="animate-spin text-[var(--saffron-600)] mx-auto" />
          ) : (
            <p className="text-2xl font-bold text-[var(--ink-0)]">{credits?.balance ?? 0}</p>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* === Weekly Summary === */}
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(99,102,241,0.1)' }}>
                <BarChart3 size={20} className="text-indigo-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Weekly Summary</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Pipeline changes, wins, losses, and action items ({FEATURE_PRICING.weekly_summary} credits)</p>
              </div>
            </div>
            <button
              onClick={handleWeeklySummary}
              disabled={summaryLoading}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50 transition-colors"
            >
              {summaryLoading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
              Generate
            </button>
          </div>

          {summary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Deals Won</p>
                <p className="text-xl font-bold text-green-500">{summary.dealsWon}</p>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Deals Lost</p>
                <p className="text-xl font-bold text-red-500">{summary.dealsLost}</p>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Revenue</p>
                <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(summary.revenueCollected)}</p>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>New Contacts</p>
                <p className="text-xl font-bold text-blue-500">{summary.newContacts}</p>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Open Tickets</p>
                <p className="text-xl font-bold text-amber-500">{summary.openTickets}</p>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--bg-inset)' }}>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Overdue Tasks</p>
                <p className="text-xl font-bold text-red-500">{summary.overdueTasks}</p>
              </div>
            </div>
          )}
        </div>

        {/* === Lead Scoring === */}
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
              <Brain size={20} className="text-green-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Lead Scoring</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Score contacts based on engagement, deals, and activity ({FEATURE_PRICING.lead_score} credits)</p>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Select Contact</label>
              <select
                value={selectedContact}
                onChange={e => setSelectedContact(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                <option value="">Choose a contact...</option>
                {contacts.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName}{c.email ? ` (${c.email})` : ''}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleLeadScore}
              disabled={scoreLoading || !selectedContact}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {scoreLoading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
              Score
            </button>
          </div>

          {scoreResult && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
              <div className="flex items-center gap-4 mb-3">
                <div className={`text-4xl font-black ${scoreColor(scoreResult.score)}`}>{scoreResult.score}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>/ 100</div>
              </div>
              {scoreResult.breakdown && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {Object.entries(scoreResult.breakdown).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {(val as number) > 0 ? <CheckCircle2 size={12} className="text-green-500" /> : <span className="h-3 w-3 rounded-full border" style={{ borderColor: 'var(--border-primary)' }} />}
                      <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}: +{val as number}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* === Email Writer === */}
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
              <Mail size={20} className="text-blue-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Email Writer</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Draft professional emails for follow-ups, intros, and reminders ({FEATURE_PRICING.email_draft} credits)</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Contact</label>
              <select
                value={emailContact}
                onChange={e => setEmailContact(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                <option value="">Choose a contact...</option>
                {contacts.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Purpose</label>
              <select
                value={emailPurpose}
                onChange={e => setEmailPurpose(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                <option value="follow_up">Follow Up</option>
                <option value="introduction">Introduction</option>
                <option value="meeting_request">Meeting Request</option>
                <option value="thank_you">Thank You</option>
                <option value="payment_reminder">Payment Reminder</option>
              </select>
            </div>
            <button
              onClick={handleEmailDraft}
              disabled={emailLoading || !emailContact}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {emailLoading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Draft
            </button>
          </div>

          {emailResult && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
              <div className="mb-2">
                <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Subject:</span>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{emailResult.subject}</p>
              </div>
              <div className="rounded-lg p-4 text-sm whitespace-pre-line" style={{ backgroundColor: 'var(--bg-inset)', color: 'var(--text-secondary)' }}>
                {emailResult.body}
              </div>
            </div>
          )}
        </div>

        {/* === Proposal Drafts & Suggestions — quick links === */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}>
                <FileText size={20} className="text-purple-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Proposal Drafts</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{FEATURE_PRICING.proposal_draft} credits per draft</p>
              </div>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Auto-generate proposal content from any Deal page. Go to Deals, open a deal, and click the AI Proposal button.
            </p>
          </div>

          <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.1)' }}>
                <Lightbulb size={20} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Smart Suggestions</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{FEATURE_PRICING.suggestions} credit per suggestion</p>
              </div>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Get context-aware suggestions on any Contact, Deal, or Invoice page. Look for the AI suggestions panel on detail pages.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
